import { TicketEventAction, type TicketTimelineEntryDto, TicketTimelineEntryKind } from "@/api/tickets";
import type { MessageValues, TranslationKey } from "@/locales/i18n";
import { remoteAttachmentView } from "./ticket-attachments";
import { formatDateTime, type TicketActivityItem } from "./ticket-presentation";
import { describeTicketEvent, ticketEventIcon } from "./ticket-timeline";

type Translate = (key: TranslationKey, values?: MessageValues) => string;
type Resolution = NonNullable<Extract<TicketActivityItem, { kind: "event" }>["resolution"]>;

const feedbackMethods: Record<string, { labelKey: TranslationKey; icon: string }> = {
  phone: { labelKey: "tickets.resolution.method.phone", icon: "tabler:phone" },
  sms: { labelKey: "tickets.resolution.method.sms", icon: "tabler:message" },
  email: { labelKey: "tickets.resolution.method.email", icon: "tabler:mail" },
  other: { labelKey: "tickets.resolution.method.other", icon: "tabler:messages" },
};

function resolutionDetails(detail: unknown, t: Translate): Resolution | undefined {
  if (!detail || typeof detail !== "object" || Array.isArray(detail)) return undefined;
  const { resolutionResult, feedbackMethod, feedbackMethodOther } = detail as Record<string, unknown>;
  if (typeof resolutionResult !== "string" || !resolutionResult.trim() || typeof feedbackMethod !== "string") return undefined;
  const method = Object.hasOwn(feedbackMethods, feedbackMethod) ? feedbackMethods[feedbackMethod] : undefined;
  if (!method) return undefined;
  if (feedbackMethod === "other" && (typeof feedbackMethodOther !== "string" || !feedbackMethodOther.trim())) return undefined;

  return {
    result: resolutionResult.trim(),
    feedbackMethodLabel: feedbackMethod === "other" ? (feedbackMethodOther as string).trim() : t(method.labelKey),
    feedbackMethodIcon: method.icon,
  };
}

/** 优先使用关联的解决记录，兼容旧服务留在事件 JSON 中的快照。 */
function readResolution(entry: TicketTimelineEntryDto, t: Translate): Resolution | undefined {
  if (entry.kind !== TicketTimelineEntryKind.Event || entry.action !== TicketEventAction.StatusChanged || entry.toValue !== "resolved") return undefined;
  const resolution = resolutionDetails(entry.resolution, t);
  if (resolution) return resolution;
  if (!entry.detailJson) return undefined;
  try {
    return resolutionDetails(JSON.parse(entry.detailJson), t);
  } catch {
    return undefined;
  }
}

/** 解决资料展开在原状态活动里，不额外生成备注或重复状态条目。 */
export function toActivityItem(entry: TicketTimelineEntryDto, t: Translate, intlLocale: string): TicketActivityItem {
  const time = formatDateTime(entry.occurredAt, intlLocale);
  if (entry.kind === TicketTimelineEntryKind.Message && entry.message) {
    const author = entry.message.authorDisplayName ?? entry.actorDisplayName ?? t("tickets.activity.systemAuthor");
    return {
      id: entry.id,
      kind: "note",
      author,
      avatarUrl: entry.message.authorAvatarUrl ?? entry.actorAvatarUrl,
      initials: author.slice(0, 1),
      content: entry.message.body,
      mentions: entry.message.mentions,
      attachments: entry.message.attachments.map(remoteAttachmentView),
      time,
    };
  }

  const author = entry.actorDisplayName?.trim() || t("tickets.activity.systemAuthor");
  return {
    id: entry.id,
    kind: "event",
    author,
    avatarUrl: entry.actorAvatarUrl,
    initials: author.slice(0, 1),
    icon: ticketEventIcon(entry.action),
    segments: describeTicketEvent(entry, t),
    time,
    resolution: readResolution(entry, t),
  };
}
